import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import moment from "moment";

import { CardLayout, DeleteModal, LedgerModeToggle, MTable } from "../v3";
import { getLedgerTransactionTableColumns, getTransactionTableColumns } from "../../pages/account/constants";
import { AccountService, AnalyticsService, FinanceService } from "../../services";
import { getAccountName } from "../../utils/formatters";
import { derivePdfColumns, getPrintableRows, TransactionTypes, useHasPermission } from "../../utils";
import {
  DELETE_ACCOUNT_TRANSACTION,
  EDIT_ACCOUNT_TRANSACTION,
  EXPORT_ACCOUNT_TRANSACTION,
  VIEW_ACCOUNT_LEDGER_MODE_STATEMENT,
} from "../../constants";
import { handlePdfDownload, Listing } from "../pdf";
import { usePersistedTableState } from "../../hooks/usePersistedTableState";

export const TransactionList = ({ name = "", onSuccess = () => {}, reloadData = 0 }) => {
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  // Remembered per visit: Back restores these, forward navigation starts from the defaults.
  const {
    searchText,
    setSearchText,
    isLedgerMode: isSavedLedgerMode,
    setLedgerMode: setIsLedgerMode,
    sortModel,
    setSortModel,
    dateRange,
    setDateRange,
    paginationModel: paginationState,
    setPaginationModel: setPaginationState,
  } = usePersistedTableState({
    tableId: "transactions",
    defaults: { sortModel: [{ field: "date", sort: "desc" }] },
  });
  const [loading, setLoading] = useState(false);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    transactionId: null,
  });

  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.user);

  const hasEditTransaction = useHasPermission(EDIT_ACCOUNT_TRANSACTION);
  const hasDeleteTransaction = useHasPermission(DELETE_ACCOUNT_TRANSACTION);
  const hasExportTransaction = useHasPermission(EXPORT_ACCOUNT_TRANSACTION);
  const hasLedgerAccess = useHasPermission(VIEW_ACCOUNT_LEDGER_MODE_STATEMENT);
  // A remembered ledger toggle never outlives the permission.
  const isLedgerMode = hasLedgerAccess && isSavedLedgerMode;

  const fetchData = useCallback(
    async (transactionFilters = {}) => {
      setLoading(true);
      try {
        const accountTransactions = isLedgerMode
          ? await AnalyticsService.getAccountLedger({ ...transactionFilters, accountId: id })
          : await FinanceService.getTransactions({ ...transactionFilters, accountId: id });

        // gets the unique accountIds to get the account name
        const accountIds = Array.from(
          new Set(accountTransactions?.data?.rows?.map((item) => [item.toAccountId, item.fromAccountId]).flat()),
        );
        if (accountIds.length > 0) {
          const { data: accountData } = await AccountService.getAccounts({
            ids: accountIds,
            includeRestrictedTypes: true,
            limit: accountIds.length,
            includeDeleted: true,
            includeOwnerDetails: true,
          });
          setAccounts(accountData?.rows);
        }

        setTransactions(accountTransactions?.data?.rows);
        setTotalCount(accountTransactions?.data?.count);
      } catch (error) {
        toast.error("Failed to fetch transactions");
        console.error("🚀 ~ fetchData ~ error:", error);
      } finally {
        setLoading(false);
      }
    },
    [id, isLedgerMode],
  );

  useEffect(() => {
    if (!dateRange) return; // wait for DateRangeInput default/apply callback

    fetchData({
      page: paginationState.page + 1,
      limit: paginationState.pageSize,
      fromDate: dateRange.startDate,
      toDate: dateRange.endDate,
      ...(!isLedgerMode && {
        sortBy: sortModel[0]?.field,
        sortOrder: sortModel[0]?.sort,
        description: searchText,
      }),
    });
  }, [fetchData, sortModel, paginationState, searchText, dateRange, reloadData, isLedgerMode]);

  const handleLedgerModeToggle = () => {
    setIsLedgerMode((prev) => !prev);
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleSearchChange = (searchValue) => {
    setSearchText(searchValue);
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handleDateRangeChange = (ranges) => {
    const selection = ranges?.[0];
    if (!selection?.startDate || !selection?.endDate) return;

    // WHY: Apply user-selected date boundaries without overriding existing pagination/sort params.
    setDateRange({
      startDate: moment(selection.startDate).startOf("day").toISOString(),
      endDate: moment(selection.endDate).endOf("day").toISOString(),
    });
    setPaginationState((prev) => ({ ...prev, page: 0 }));
  };

  const handlePaginationChange = (model) => {
    setPaginationState(model);
  };

  const handleDownloadPdfClick = async () => {
    setIsPdfGenerating(true);
    try {
      const pdfColumnHeaders = derivePdfColumns(columnHeaders);
      const printableRows = getPrintableRows(rows);

      const pdfDocument = (
        <Listing
          title={`Transactions : ${name}`}
          columnHeaders={pdfColumnHeaders}
          tableRows={printableRows}
          company={user?.company}
        />
      );

      await handlePdfDownload({
        document: pdfDocument,
        pdfTitle: `Transactions : ${name}`,
      });
    } catch (error) {
      toast.error("PDF generation failed");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const onViewClick = (params, event) => {
    navigate(`/transaction/${params.row.id}`);
  };

  const openDeleteModal = (account) => {
    setDeleteModal({
      isOpen: true,
      transactionId: account?.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      transactionId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await FinanceService.deleteTransaction(deleteModal.transactionId);
      toast.success("Transaction deleted successfully");
      closeDeleteModal();
      onSuccess?.();
    } catch (error) {
      toast.error("Failed to delete transaction");
      console.error("🚀 ~ handleDelete ~ error:", error);
    }
  };

  const handleActions = async (transactionData, actionType) => {
    switch (actionType) {
      case "edit":
        const { id, type, toAccountId } = transactionData;
        switch (type) {
          case TransactionTypes.ACCOUNT:
            navigate(`/transaction/edit/${id}?accountId=${toAccountId}`);
            break;
          case TransactionTypes.WORKER:
            const { data: toAccount } = await AccountService.getAccountById(toAccountId);
            navigate(`/transaction/edit/${id}?workerId=${toAccount?.ownerEmployee?.id}`);
            break;
          case TransactionTypes.VENDOR:
            const { data: toAccountVendor } = await AccountService.getAccountById(toAccountId);
            navigate(`/transaction/edit/${id}?vendorId=${toAccountVendor?.ownerVendor?.id}`);
            break;
          default:
            break;
        }
        break;
      case "delete":
        openDeleteModal(transactionData);
        break;
      default:
        break;
    }
  };

  const columnHeaders = isLedgerMode
    ? getLedgerTransactionTableColumns(handleActions, hasEditTransaction, hasDeleteTransaction)
    : getTransactionTableColumns(handleActions, hasEditTransaction, hasDeleteTransaction);

  const ledgerModeToggle = hasLedgerAccess ? (
    <LedgerModeToggle isLedgerMode={isLedgerMode} onToggle={handleLedgerModeToggle} />
  ) : null;

  const rows = transactions.map((transaction, index) => {
    const account = accounts.find(
      (account) =>
        account.id !== Number(id) && (account.id === transaction.fromAccountId || account.id === transaction.toAccountId),
    );
    return {
      id: transaction?.id,
      sl: index + 1 + paginationState.page * paginationState.pageSize,
      date: transaction?.date ? moment(transaction.date).format("DD MMM YYYY") : "-",
      description: transaction?.description,
      cashIn: transaction?.toAccountId === Number(id) ? transaction?.amount : null,
      cashOut: transaction?.fromAccountId === Number(id) ? transaction?.amount : null,
      fromOrToAccount: getAccountName(account?.type, account),
      toAccountId: transaction?.toAccountId,
      balance: transaction?.balance,
      type: transaction?.type || {},
    };
  });

  const DEFAULT_SORT = [{ field: "date", sort: "desc" }];

  return (
    <>
      <CardLayout>
        <MTable
          // Search data
          placeholder="Search by description"
          searchText={searchText}
          onSearchChange={handleSearchChange}
          showSearchBar
          disableSearchBar={isLedgerMode}
          startAction={ledgerModeToggle}
          // Date range data
          showDateRangePicker
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          // Export data
          showExportButton={hasExportTransaction}
          isPdfGenerating={isPdfGenerating}
          onExportPdfClick={handleDownloadPdfClick}
          // Table data
          columnHeaders={columnHeaders}
          tableRows={rows}
          rowCount={totalCount}
          loading={loading}
          sortModel={sortModel}
          onSortChange={setSortModel}
          defaultSortModel={DEFAULT_SORT}
          disableSorting={isLedgerMode}
          paginationModel={paginationState}
          onPaginationChange={handlePaginationChange}
          onRowClick={onViewClick}
        />
      </CardLayout>

      {/* Delete Confirmation Modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this transaction?"
      />
    </>
  );
};
